import FormLabel from '@rio-cloud/rio-uikit/FormLabel';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import classNames from 'classnames';
import { FunctionComponent, useId } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { MeansOfTransportType } from '../../../../sharedComponents/common.types';
import { InputSign } from '../../../../sharedComponents/InputSign';
import { OptionalLabel } from '../../../../sharedComponents/OptionalLabel';
import { TextInput } from '../../../../sharedComponents/sidebar/TextInput';
import { TransportInformation } from '../../../compoundManagement.types';

type TransportInformationInputsProps = {
    transportInformation: TransportInformation;
    setTransportInformation: (newValues: Partial<Omit<TransportInformation, 'meansOfTransportType'>>) => void;
    validationErrorMessageIds?: { meansOfTransportId: string | undefined };
    disabled: boolean;
};

export const isTransportInformation = (partial: Partial<TransportInformation>): partial is TransportInformation => {
    return partial.meansOfTransportType !== undefined;
};

export const TransportInformationInputs: FunctionComponent<TransportInformationInputsProps> = (props) => {
    switch (props.transportInformation.meansOfTransportType) {
        case MeansOfTransportType.TRUCK:
            return <TruckInputs {...props} />;
        case MeansOfTransportType.OWN_WHEELS:
            return null;
        case MeansOfTransportType.RORO_VESSEL:
        case MeansOfTransportType.CONTAINER_VESSEL:
            return <VesselInputs {...props} />;
        case MeansOfTransportType.TRAIN:
            return <TrainInputs {...props} />;
    }
};

const TruckInputs: FunctionComponent<TransportInformationInputsProps> = ({
    transportInformation,
    setTransportInformation,
    validationErrorMessageIds,
    disabled,
}) => {
    const truckInputId = useId();
    const trailerInputId = useId();
    const intl = useIntl();
    const hasError = validationErrorMessageIds?.meansOfTransportId !== undefined;

    return (
        <>
            <div className={classNames('form-group', { 'has-feedback': hasError, 'has-error': hasError })}>
                <FormLabel htmlFor={truckInputId}>
                    <FormattedMessage id="outboundOrderBook.compoundManagement.sidebar.truckLicensePlate" />
                    <TransportInformationTooltip
                        disabled={disabled}
                        inputField={transportInformation.meansOfTransportId}
                    />
                </FormLabel>
                <TextInput
                    id={truckInputId}
                    placeholder={intl.formatMessage({
                        id: 'outboundOrderBook.compoundManagement.sidebar.truckLicensePlate.placeholder',
                    })}
                    value={transportInformation.meansOfTransportId ?? ''}
                    setValue={(meansOfTransportId) => setTransportInformation({ meansOfTransportId })}
                    hasError={hasError}
                    disabled={disabled}
                />
                {hasError && (
                    <InputSign messageId={validationErrorMessageIds.meansOfTransportId!} glyph="rioglyph-error-sign" />
                )}
            </div>
            <div className="form-group">
                <OptionalLabel htmlFor={trailerInputId} isOptional>
                    <FormattedMessage id="outboundOrderBook.compoundManagement.sidebar.trailerId" />
                    <TransportInformationTooltip disabled={disabled} inputField={transportInformation.trailerId} />
                </OptionalLabel>
                <TextInput
                    id={trailerInputId}
                    placeholder={intl.formatMessage({
                        id: 'outboundOrderBook.compoundManagement.sidebar.trailerId.placeholder',
                    })}
                    value={transportInformation.trailerId ?? ''}
                    setValue={(trailerId) => setTransportInformation({ trailerId })}
                    disabled={disabled}
                />
            </div>
        </>
    );
};

const VesselInputs: FunctionComponent<TransportInformationInputsProps> = ({
    transportInformation,
    setTransportInformation,
    disabled,
}) => {
    const vesselNumberInputId = useId();
    const intl = useIntl();

    return (
        <>
            <div className="form-group">
                <FormLabel htmlFor={vesselNumberInputId}>
                    <FormattedMessage id="outboundOrderBook.compoundManagement.sidebar.vesselNumber" />
                    <TransportInformationTooltip
                        disabled={disabled}
                        inputField={transportInformation.meansOfTransportId}
                    />
                </FormLabel>
                <TextInput
                    id={vesselNumberInputId}
                    placeholder={intl.formatMessage({
                        id: 'outboundOrderBook.compoundManagement.sidebar.vesselNumber.placeHolder',
                    })}
                    value={transportInformation.meansOfTransportId ?? ''}
                    setValue={(meansOfTransportId) => setTransportInformation({ meansOfTransportId })}
                    disabled={disabled}
                />
            </div>
        </>
    );
};

const TrainInputs: FunctionComponent<TransportInformationInputsProps> = ({
    transportInformation,
    setTransportInformation,
    validationErrorMessageIds,
    disabled,
}) => {
    const wagonNumberInputId = useId();
    const intl = useIntl();
    const hasError = validationErrorMessageIds?.meansOfTransportId !== undefined;

    return (
        <div className={classNames('form-group', { 'has-feedback': hasError, 'has-error': hasError })}>
            <FormLabel htmlFor={wagonNumberInputId}>
                <FormattedMessage id="outboundOrderBook.compoundManagement.sidebar.wagonNumber" />
                <TransportInformationTooltip disabled={disabled} inputField={transportInformation.meansOfTransportId} />
            </FormLabel>
            <TextInput
                id={wagonNumberInputId}
                placeholder={intl.formatMessage({
                    id: 'outboundOrderBook.compoundManagement.sidebar.wagonNumber.placeHolder',
                })}
                value={transportInformation.meansOfTransportId ?? ''}
                setValue={(meansOfTransportId) => setTransportInformation({ meansOfTransportId })}
                hasError={hasError}
                disabled={disabled}
            />
            {hasError && (
                <InputSign messageId={validationErrorMessageIds.meansOfTransportId!} glyph="rioglyph-error-sign" />
            )}
        </div>
    );
};

type TransportInformationTooltipProps = {
    disabled: boolean;
    inputField: string | undefined;
};

const TransportInformationTooltip: FunctionComponent<TransportInformationTooltipProps> = ({ disabled, inputField }) => {
    return (
        !disabled &&
        inputField !== undefined && (
            <OverlayTrigger
                key="transportInformationTooltip"
                placement="right"
                overlay={
                    <Tooltip width={300} allowOnTouch>
                        <FormattedMessage id="outboundOrderBook.compoundManagement.sidebar.transportInformation.tooltip" />
                    </Tooltip>
                }
            >
                <span
                    data-testid="transport-information-tooltip"
                    className="rioglyph rioglyph-info-sign text-size-18 text-color-info margin-left-5"
                />
            </OverlayTrigger>
        )
    );
};
